import {
  Copy,
  Flex,
  Square,
  SvgCaretDown,
  SvgCaretUp,
} from '@grubhubprod/gh-cookbook-react';
import { Stack } from '@grubhubprod/gh-cookbook-react/dist';
import classnames from 'classnames';
import React, { useEffect, useState } from 'react';

export type AccordionProps = {
  disabled?: boolean;
  defaultExpanded?: boolean;
  expanded?: boolean;
  onChange?: () => void;
  header: (isActive: boolean) => JSX.Element;
  body: (isActive: boolean) => JSX.Element;
};

export const Accordion = ({
  defaultExpanded = false,
  disabled = false,
  onChange,
  expanded,
  header,
  body,
}: AccordionProps): JSX.Element => {
  const [isOpen, setIsOpen] = useState(defaultExpanded);
  const enabled = !disabled;
  const isControlled = expanded !== undefined;
  const isUncontrolled = expanded === undefined;

  useEffect(() => {
    // The 'expanded' prop enables parent component to control open/close state of the Accordion.
    // If 'expanded' is defined (a value is passed), this Accordion is in 'controlled' mode and
    // the prop value directly dictates whether the Accordion is open or not.
    // When 'expanded' prop changes, 'isOpen' state is synced with it to match the visibility state.
    executeIfEnabled(() => {
      if (isControlled) {
        setIsOpen(expanded);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  const handleClick = (): void => {
    onChange?.();
    if (isUncontrolled) {
      setIsOpen(!isOpen);
    }
  };

  function executeIfEnabled(cb: () => void): void {
    if (enabled) {
      cb();
    }
  }

  return (
    <Square inset="md" {...(isOpen && { dimension: '2' })} width="100%">
      <Stack
        y={isOpen ? 'x-sm' : 'none'}
        onClick={() => executeIfEnabled(handleClick)}
        className={classnames({
          cursorPointer: enabled,
          cursorNotAllowed: disabled,
        })}
      >
        <Flex justifyContent="space-between">
          <Stack x="md">
            <Flex>{header(isOpen)}</Flex>
          </Stack>
          <Copy
            variant="body"
            color={classnames({
              interactive: enabled,
              secondaryText: disabled,
            })}
          >
            {isOpen ? (
              <SvgCaretUp pointerEvents="none" />
            ) : (
              <SvgCaretDown pointerEvents="none" />
            )}
          </Copy>
        </Flex>
      </Stack>
      <Stack
        height={classnames({
          auto: isOpen,
          0: !isOpen,
        })}
        style={{ visibility: isOpen ? 'visible' : 'hidden' }}
        overflow={classnames({
          initial: isOpen,
          hidden: !isOpen,
        })}
      >
        {body(isOpen)}
      </Stack>
    </Square>
  );
};
